// THE FOLDER-NAME RULE — one home, both ends.
//
// BRIEF 140, THE RECONCILE (RULED, Laurent, 2026-08-27). §2's finalize is fired
// by the client when the undo window expires; a tab closed inside that window
// skips it, and the case is then converted correctly while its folder keeps the
// prospect name forever. The reconcile OFFERS the rename afterwards — never
// automatically — and to know whether to offer, the BROWSER has to answer a
// question only the server used to be able to: does this folder's name still
// carry the former ref?
//
// IT CANNOT BE ANSWERED WITH THE RAW REF, and that is the measurement this
// module exists for. `sanitizeFolderName` replaces the characters SharePoint
// refuses — `/` among them — so a matter whose ref is "0/0001" gets a folder
// named "Acme - 0-0001". A condition written against the stored ref would
// therefore NEVER FIRE, silently, and the feature would look built and do
// nothing. The rule has to be the same rule at both ends.
//
// So it lives here, in the shape this tree already uses for exactly that:
// `src/lib/caseRefPattern.mjs` is a pure ESM module the client imports and
// `server/src/lib/caseRefSettings.js` requires, so an allocated ref and a
// pre-filled one cannot drift. `server/src/lib/m365.js` requires this one and
// re-exports `sanitizeFolderName` under its own name, so its five call sites
// and its public export are untouched — this is an EXTRACTION, not a change:
// the regex below is byte-for-byte the one that was in m365.js.

/** Storage-side normalisation: strip the characters SharePoint refuses from a
 *  folder name, collapse whitespace, trim. `/` and `\` become `-`, which is
 *  why a ref never appears in a folder name verbatim. */
export function sanitizeFolderName(name) {
  return String(name || '').replace(/[\/\\:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

/** Phase 13's name builder, extracted with the sanitiser and for the same
 *  reason: the reconcile's offer PROMISES a name ("Rename it to …?") and the
 *  server is what will produce it, so the two must be one function. Substitutes
 *  {title}/{ref} in the configured template, collapses whitespace, trims
 *  dangling separators so an empty placeholder does not leave a stray " - ",
 *  and falls back to ref/title when the result is empty.
 *
 *  IT DOES NOT SANITISE — measured, and it matters: the on-disk name is
 *  `sanitizeFolderName(buildCaseFolderName(…))`, which is the pair
 *  `createCaseFolder` and `renameFolder` apply. Callers that need the name a
 *  folder will actually CARRY must compose both, as they do. */
export function buildCaseFolderName(template, { ref, title }) {
  const tmpl = (template && String(template).trim()) ? String(template) : '{title} - {ref}';
  let name = tmpl
    .replace(/\{title\}/g, String(title == null ? '' : title))
    .replace(/\{ref\}/g, String(ref == null ? '' : ref));
  name = name.replace(/\s+/g, ' ').trim().replace(/^[\s\-]+|[\s\-]+$/g, '').trim();
  return name || String(ref || title || '').trim();
}

/** The name a matter's folder would CARRY if it were named now: the two steps
 *  the server pipeline applies, composed once so no caller has to remember
 *  that there are two. */
export function folderNameOnDisk(template, { ref, title }) {
  return sanitizeFolderName(buildCaseFolderName(template, { ref, title }));
}

/** The last segment of a stored drive-relative folder path — the part a rename
 *  changes, and the only part a folder NAME question is about. */
export function folderLastSegment(folderPath) {
  if (!folderPath) return null;
  const parts = String(folderPath).split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : String(folderPath);
}

/**
 * THE RECONCILE'S CONDITION, and it is deliberately this narrow.
 *
 * True iff a former ref exists AND the folder's own last path segment still
 * contains it, spelled the way a folder name spells it. Nothing else.
 *
 * WHAT IT REFUSES TO BE, recorded because it is the tempting version: "the
 * name differs from what the template would produce". That condition fires on
 * any matter whose title was ever edited — which is most of them on a real
 * firm's tree — and turns a precise offer into a permanent nag. A folder the
 * firm renamed ON PURPOSE is not wrong, and this must never say it is.
 *
 * @param {string|null|undefined} folderPath  the matter's stored caseRootFolder
 * @param {string|null|undefined} formerRef   the ref it carried as a prospect
 * @returns {boolean}
 */
export function folderRefIsStale(folderPath, formerRef) {
  const seg = folderLastSegment(folderPath);
  if (!seg || !formerRef) return false;
  const spelled = sanitizeFolderName(formerRef);
  if (!spelled) return false;
  return seg.includes(spelled);
}
